import React from "react";

/**
 * Component for displaying error label with exclamation icon and text
 * @param {Object} props
 * @param {string} props.children - Error message text
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.size - Icon size ("sm", "md", "lg" or default "md")
 * @param {boolean} props.standalone - If true, renders only the icon without wrapper
 * @param {string} props.fieldName - Field name to display in bold (e.g., "First Name:")
 */
function ErrorLabel({
  children,
  className = "",
  size = "md",
  standalone = false,
  fieldName,
}) {
  // Determine icon size class
  const getIconSizeClass = () => {
    switch (size) {
      case "sm":
        return "error-icon-sm";
      case "lg":
        return "error-icon-lg";
      default:
        return ""; // Default size (16px) is already in .error-icon
    }
  };

  const iconSizeClass = getIconSizeClass();

  if (standalone) {
    return (
      <span
        className={`mFont error-icon ${iconSizeClass}`}
        aria-label="Error"
        role="img"
      >
        error
      </span>
    );
  }

  // Normalize message text
  const normalizeMessage = (msg, label) => {
    if (typeof msg !== "string") return msg;
    let out = msg;
    if (label) {
      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const prefix = new RegExp(`^${escaped}\\s*:?\\s*`, "i");
      out = out.replace(prefix, "");
    }
    // Common schema messages → concise form
    if (/zip\s*code\s*is\s*required/i.test(out)) {
      out = "is required";
    }
    // Zod generic type errors → required
    if (/expected\s+\w+\s*,\s*received\s*(null|undefined)/i.test(out)) {
      out = "is required";
    }
    if (/please\s*select\s*at\s*least\s*one\s*years?/i.test(out)) {
      out = "Please select at least one year.";
    }
    if (/please\s*select\s*at\s*least\s*one\s*states?/i.test(out)) {
      out = "Please select at least one state.";
    }
    return out.trim();
  };

  return (
    <div className={`form-error ${className}`}>
      <span
        className={`mFont error-icon ${iconSizeClass}`}
        aria-label="Error"
        role="img"
      >
        error
      </span>
      {(() => {
        const message = normalizeMessage(children, fieldName);
        if (fieldName) {
          return (
            <>
              <strong>{fieldName}</strong>{" "}
              {typeof message === "string" ? message : ""}
            </>
          );
        }
        // Even without a label, show the normalized message instead of the raw children
        return typeof message === "string" ? message : children ?? null;
      })()}
    </div>
  );
}

export default ErrorLabel;
